import type { KnowledgeView, KnowledgeViewKey, ViewNavigation } from "../knowledge/types";
import {
  ALL_FEATURES,
  type Entitlements,
  type EntitlementsConfig,
  type FeatureKey,
} from "./types";

/**
 * Build an {@link Entitlements} from a declarative config. Anything not
 * specified defaults to enabled (all-enabled is the shipped behaviour until a
 * backend `GET /entitlements` exists). This is the single seam a future
 * tier/plan resolver plugs into — components never branch on tier names.
 */
export function createEntitlements(config: EntitlementsConfig = {}): Entitlements {
  const features = config.features ?? {};
  const allowedViews = config.views;
  return {
    ...(config.tier ? { tier: config.tier } : {}),
    isFeatureEnabled(feature: FeatureKey): boolean {
      return features[feature] ?? true;
    },
    isViewEnabled(view: KnowledgeViewKey): boolean {
      // The Reference Layer is a deterministic, graph-derived technical
      // reference — not an AI-narrated audience lens — so it is never
      // plan-gated: if the run produced it, every plan can read it.
      if (view === "reference") return true;
      if (!this.isFeatureEnabled("knowledge_views")) return false;
      if (!allowedViews || allowedViews === "all") return true;
      return allowedViews.includes(view);
    },
  };
}

/**
 * All capabilities enabled. The ambient default, and correct for the public
 * preview: that build has no control plane to ask, and every capability it can
 * exercise is already baked into the static bundle it was given.
 *
 * It is **not** correct for an authenticated console whose resolution has not
 * arrived. See {@link NONE_ENABLED}.
 */
export const ALL_ENABLED: Entitlements = createEntitlements();

/**
 * No capability enabled. What an authenticated surface uses while its plan is
 * still being resolved, and when resolution failed.
 *
 * Defaulting to all-enabled in those two states was a fail-open: an unresolved
 * plan rendered exactly like an unlimited one, so a capability the workspace does
 * not have appeared briefly (or, on a failed resolution, permanently) as an
 * offer. Unknown must read as "not yet", never as "yes" — and a surface tells the
 * two apart from the resolution *status*, not from the capability answer, so
 * "resolving" is never presented to a reader as "not in your plan".
 */
export const NONE_ENABLED: Entitlements = createEntitlements({
  features: Object.fromEntries(ALL_FEATURES.map((f) => [f, false])) as Partial<
    Record<FeatureKey, boolean>
  >,
  views: [],
});

/**
 * How the active capability set was arrived at.
 *
 * Carried beside the capabilities because a denied answer and an unresolved one
 * demand different words on screen while producing identical booleans.
 */
export type EntitlementsStatus =
  /** Resolved from the control plane for this workspace. */
  | "resolved"
  /** Still in flight. Capabilities are closed, but nothing is denied yet. */
  | "loading"
  /** Resolution failed. Capabilities stay closed and the failure is shown. */
  | "error"
  /** No workspace in scope (or no backend): the build's ambient default. */
  | "ambient";

export interface ClassifiedViews {
  /** Views present in the run AND enabled by the plan. */
  readonly available: readonly KnowledgeView[];
  /** Views present in the run but gated by the plan (show as locked + upgrade hint). */
  readonly locked: readonly KnowledgeView[];
}

/**
 * Split the navigation's views into available vs plan-locked. Availability is
 * data-driven (only views the run actually produced are considered), then the
 * entitlements gate is applied on top.
 */
export function classifyViews(nav: ViewNavigation, entitlements: Entitlements): ClassifiedViews {
  const available: KnowledgeView[] = [];
  const locked: KnowledgeView[] = [];
  for (const view of nav.views) {
    if (entitlements.isViewEnabled(view.key)) available.push(view);
    else locked.push(view);
  }
  return { available, locked };
}
